<div class="clusters">
  <header class="clusters-header">
    <div class="clusters-title-row">
      <div>
        <h1>Clusters</h1>
        <p class="clusters-subtitle" *ngIf="domain">Organize {{ domain }} into topic pillars before committing to Inventory Plan.</p>
      </div>
      <div class="clusters-actions" *ngIf="ready && pillars.length > 0">
        <button
          type="button"
          class="btn"
          (click)="loadProposal()"
          [disabled]="applying || previewLoading"
          title="Rebuild this organization from suggested clusters, discarding the current draft"
        >Start from suggestions</button>
        <button type="button" class="btn" (click)="discardDraft()" [disabled]="applying">Discard</button>
        <button type="button" class="btn btn--accent" (click)="openApply()" [disabled]="applying || previewLoading">
          {{ applying ? 'Applying…' : 'Apply organization' }}
        </button>
      </div>
    </div>
  </header>

  <section class="card card--pad" *ngIf="!domain">
    <p class="muted">No domain selected. Choose an active domain in the header.</p>
  </section>

  <section class="card card--pad" *ngIf="domain && loading && !status">
    <p class="muted">Loading…</p>
  </section>

  <p class="error" *ngIf="error">{{ error }}</p>

  <section class="card card--pad blocked-card" *ngIf="domain && status && !status.ready">
    <h2>Prerequisites not met</h2>
    <ul>
      <li *ngFor="let reason of status.blockedReasons">{{ reason }}</li>
    </ul>
    <button type="button" class="btn btn--accent" (click)="goToSetup()">Open Setup</button>
  </section>

  <section class="card card--pad demand-banner" *ngIf="status?.ready && preview && preview.summary.demandLabelCount < preview.summary.trackedUrlCount * 0.5">
    <p class="muted">
      Demand labels are sparse for this domain ({{ preview.summary.demandLabelCount }} / {{ preview.summary.trackedUrlCount }} URLs).
      Run page demand labeling in Setup for better claim-alignment scoring.
    </p>
  </section>

  <section class="card card--pad proposal-meta-banner" *ngIf="proposalMeta && proposalMeta.skippedThinTopics.length > 0">
    <p class="muted">
      Suggestions skipped {{ proposalMeta.skippedThinTopics.length }} thin topic(s) with fewer than
      {{ proposalMeta.minUrlCountApplied }} URLs ({{ proposalMeta.trackedUrlCount }} tracked).
      You can still add them manually via <strong>+ Add</strong>.
    </p>
  </section>

  <section class="clusters-summary card card--pad" *ngIf="preview">
    <div class="summary-stats">
      <span><strong>{{ preview.summary.pillarCount }}</strong> pillars</span>
      <span><strong>{{ preview.summary.assignedUrlCount }}</strong> assigned</span>
      <span><strong>{{ preview.summary.unassignedUrlCount }}</strong> unassigned</span>
      <span><strong>{{ preview.summary.trackedUrlCount }}</strong> tracked URLs</span>
    </div>
  </section>

  <div class="clusters-workspace" *ngIf="ready && pillars.length > 0">
    <aside class="pillars-panel card">
      <header class="card__header">
        <h2 class="card__title">Pillars</h2>
        <button type="button" class="btn btn--sm" (click)="openAddPillar()" [disabled]="pillars.length >= 12">+ Add</button>
      </header>
      <div class="card__body">
      <div class="pillar-actions-row" *ngIf="pillars.length >= 2">
        <button type="button" class="btn btn--sm" (click)="openMerge()">Merge…</button>
      </div>
      <ul class="pillar-list">
        <li
          *ngFor="let pillar of pillars; trackBy: trackPillar"
          class="pillar-item"
          [class.pillar-item--active]="selectedPillarSlug === pillar.topicSlug"
          (click)="selectPillar(pillar.topicSlug)"
        >
          <div class="pillar-item-main">
            <strong>{{ pillar.label }}</strong>
            <span class="muted pillar-slug">{{ pillar.topicSlug }}</span>
          </div>
          <div class="pillar-item-meta">
            <span class="badge badge--pill">{{ urlCountForPillar(pillar.topicSlug) }} URLs</span>
          </div>
          <div
            class="pillar-overlap-hints"
            *ngIf="similarHintsForPillar(pillar.topicSlug).length > 0"
            (click)="$event.stopPropagation()"
          >
            <p class="overlap-hint muted" *ngFor="let hint of similarHintsForPillar(pillar.topicSlug)">
              Similar to <strong>{{ hint.label }}</strong> · {{ hint.reasonLabel }}
              <button
                type="button"
                class="btn btn--link"
                (click)="openMergeFromHint(pillar.topicSlug, hint.topicSlug)"
              >
                Merge…
              </button>
            </p>
          </div>
          <ul
            class="pillar-subtopic-preview"
            *ngIf="selectedPillarSlug === pillar.topicSlug && subtopicsForPillar(pillar.topicSlug).length > 0"
          >
            <li *ngFor="let sub of subtopicsForPillar(pillar.topicSlug)">
              {{ sub.label }} ({{ sub.urlCount }})
            </li>
          </ul>
          <div class="pillar-item-buttons" (click)="$event.stopPropagation()">
            <button type="button" class="btn btn--link" (click)="openSplit(pillar.topicSlug)">Split</button>
            <button type="button" class="btn btn--link danger" (click)="removePillar(pillar.topicSlug)">Remove</button>
          </div>
        </li>
        <li
          class="pillar-item pillar-item--unassigned"
          [class.pillar-item--active]="selectedPillarSlug === '__unassigned__'"
          (click)="selectPillar('__unassigned__')"
        >
          <strong>Unassigned</strong>
          <span class="badge badge--pill">{{ preview?.summary?.unassignedUrlCount || 0 }} URLs</span>
        </li>
      </ul>
      </div>
    </aside>

    <main class="urls-panel card">
      <header class="card__header">
        <h2 class="card__title">URLs</h2>
        <input type="search" class="url-search input" placeholder="Filter URLs…" [(ngModel)]="urlSearch" />
      </header>
      <div class="card__body">

      <section
        class="pillar-composition"
        *ngIf="!previewLoading && selectedPillarPreview && selectedPillarSlug !== '__unassigned__'"
      >
        <h3 class="composition-title">What this cluster is made of</h3>
        <ng-container *ngIf="selectedPillarPreview.composition.mode === 'distinct_subtopics'">
          <p class="composition-lead muted">{{ selectedPillarPreview.composition.message }}</p>
          <ul class="composition-subtopics">
            <li *ngFor="let sub of selectedPillarPreview.composition.subtopics">
              <span class="composition-subtopic-label">{{ sub.label }}</span>
              <span class="composition-subtopic-meta muted">{{ sub.urlCount }} URLs · {{ sub.sharePct }}%</span>
            </li>
          </ul>
          <p class="composition-footnote muted" *ngIf="selectedPillarPreview.composition.primaryFocusUrlCount > 0">
            {{ selectedPillarPreview.composition.primaryFocusUrlCount }} other
            {{ selectedPillarPreview.composition.primaryFocusUrlCount === 1 ? 'page' : 'pages' }}
            align primarily with {{ selectedPillarPreview.label }} itself.
          </p>
        </ng-container>
        <p class="composition-homogeneous muted" *ngIf="selectedPillarPreview.composition.mode === 'homogeneous'">
          {{ selectedPillarPreview.composition.message }}
        </p>
      </section>

      <p class="muted" *ngIf="previewLoading">Updating preview…</p>
      <table class="table table--dense" *ngIf="!previewLoading">
        <thead>
          <tr>
            <th>URL</th>
            <th>Class</th>
            <th>Score</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            *ngFor="let row of visibleUrls; trackBy: trackUrl"
            class="table__row"
            [class.table__row--selected]="selectedUrlId === row.urlId"
            (click)="selectUrl(row.urlId)"
          >
            <td class="url-cell">
              <a [href]="row.url" target="_blank" rel="noopener" (click)="$event.stopPropagation()">{{ row.url }}</a>
              <span class="badge badge--accent badge--pill" *ngIf="row.pinned">pinned</span>
            </td>
            <td>{{ row.topicClass || '—' }}</td>
            <td>{{ formatScore(row.score) }}</td>
            <td class="url-actions" (click)="$event.stopPropagation()">
              <select class="input" #moveSelect (change)="moveUrlToPillar(row.urlId, moveSelect.value); moveSelect.value = ''">
                <option value="">Move to…</option>
                <option *ngFor="let p of pillars" [value]="p.topicSlug">{{ p.label }}</option>
              </select>
              <button type="button" class="btn btn--link" *ngIf="row.pinned" (click)="unpinUrl(row.urlId)">Unpin</button>
            </td>
          </tr>
        </tbody>
      </table>
      <p class="muted" *ngIf="!previewLoading && visibleUrls.length === 0">No URLs in this bucket.</p>
      </div>
    </main>

    <aside class="inspector-panel card card--pad" *ngIf="selectedUrl as url">
      <h2>Why this URL?</h2>
      <p class="inspector-url"><a [href]="url.url" target="_blank" rel="noopener">{{ url.url }}</a></p>
      <p><strong>Score:</strong> {{ formatScore(url.score) }}</p>
      <p><strong>Class:</strong> {{ url.topicClass || '—' }}</p>
      <p *ngIf="url.pinned"><strong>Assignment:</strong> Manual pin</p>
      <h3>Signals</h3>
      <ul class="reason-list" *ngIf="url.reasons.length > 0">
        <li *ngFor="let reason of url.reasons">
          <span class="reason-weight">{{ formatScore(reason.weight) }}</span>
          {{ reason.label }}
        </li>
      </ul>
      <p class="muted" *ngIf="url.reasons.length === 0">No scoring signals (pinned or below threshold).</p>
    </aside>
  </div>

  <section class="card competitor-inspiration" *ngIf="ready && pillars.length > 0">
    <header class="card__header">
      <div>
        <h2 class="card__title">Competitor organization</h2>
        <p class="muted competitor-inspiration-lead">
          Topics your marked competitors emphasize — grouped by similar themes. Compare page counts and add pillars for inspiration.
        </p>
      </div>
      <div class="competitor-inspiration-actions">
        <button
          type="button"
          class="btn btn--sm"
          *ngIf="competitorInspiration && (competitorInspiration.buckets?.length ?? 0) > 0"
          (click)="toggleCompetitorViewMode()"
        >
          {{ competitorViewMode === 'grouped' ? 'Flat list' : 'Grouped view' }}
        </button>
        <button
          type="button"
          class="btn btn--sm"
          (click)="loadCompetitorInspiration()"
          [disabled]="competitorInspirationLoading"
        >
          Refresh
        </button>
      </div>
    </header>

    <div class="card__body">
    <p class="muted" *ngIf="competitorInspirationLoading">Loading competitor topics…</p>
    <p class="error" *ngIf="competitorInspirationError">{{ competitorInspirationError }}</p>

    <p class="muted" *ngIf="!competitorInspirationLoading && competitorInspiration?.competitors?.length === 0">
      No marked competitors for this domain.
      <button type="button" class="btn btn--link" (click)="goToSetup()">Add competitors in Setup</button>
    </p>

    <p
      class="muted"
      *ngIf="
        !competitorInspirationLoading &&
        competitorInspiration &&
        competitorInspiration.competitors.length > 0 &&
        competitorInspiration.topics.length === 0
      "
    >
      Competitors are marked but none have topic data yet — run topic prep in Setup.
    </p>

    <div
      class="competitor-chips"
      *ngIf="competitorInspiration && competitorInspiration.competitors.length > 0"
    >
      <span
        class="badge badge--pill"
        *ngFor="let c of competitorInspiration.competitors"
        [class.badge--warning]="!c.ready"
      >
        {{ c.domain }} · {{ c.ready ? c.topicCount + ' topics' : 'needs topic prep' }}
      </span>
    </div>

    <table
      class="table table--dense"
      *ngIf="
        !competitorInspirationLoading &&
        competitorInspiration &&
        competitorViewMode === 'flat' &&
        competitorInspiration.topics.length > 0
      "
    >
      <thead>
        <tr>
          <th>Topic</th>
          <th>You</th>
          <th>Best competitor</th>
          <th>Gap</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let row of competitorInspiration.topics">
          <td>
            <strong>{{ row.label }}</strong>
            <span class="muted competitor-slug">{{ row.topicSlug }}</span>
          </td>
          <td>{{ row.ourPageCount }}</td>
          <td>
            <strong>{{ row.maxCompetitorPageCount }}</strong>
            <span class="muted competitor-best-domain">{{ topCompetitorLabel(row) }}</span>
          </td>
          <td>
            <span [class.gap-positive]="row.gap > 0">+{{ row.gap }}</span>
          </td>
          <td>
            <button
              type="button"
              class="btn btn--sm"
              *ngIf="!row.alreadyPillar"
              (click)="addPillarFromCompetitor(row.topicSlug, row.label)"
              [disabled]="pillars.length >= 12"
            >
              Add as pillar
            </button>
            <span class="muted" *ngIf="row.alreadyPillar">In your pillars</span>
          </td>
        </tr>
      </tbody>
    </table>

    <div
      class="competitor-buckets"
      *ngIf="
        !competitorInspirationLoading &&
        competitorInspiration &&
        competitorViewMode === 'grouped' &&
        (competitorInspiration.buckets?.length ?? 0) > 0
      "
    >
      <p class="muted competitor-buckets-note">
        “You” totals in a group may overlap when pages mention multiple related topics.
      </p>
      <article
        class="competitor-bucket"
        *ngFor="let bucket of competitorInspiration.buckets"
        [class.competitor-bucket--expanded]="isCompetitorBucketExpanded(bucket.bucketId)"
      >
        <header class="competitor-bucket-head" (click)="toggleCompetitorBucket(bucket.bucketId)">
          <div class="competitor-bucket-title">
            <button type="button" class="competitor-bucket-toggle btn btn--bare btn--sm" aria-label="Expand bucket">
              {{ isCompetitorBucketExpanded(bucket.bucketId) ? '▾' : '▸' }}
            </button>
            <div>
              <strong>{{ bucket.label }}</strong>
              <span class="muted competitor-bucket-meta" *ngIf="bucket.topicCount > 1">
                {{ bucket.topicCount }} topics · {{ bucketGroupingLabel(bucket) }}
              </span>
              <span class="muted competitor-slug" *ngIf="bucket.topicCount === 1">{{ bucket.representativeTopicSlug }}</span>
            </div>
          </div>
          <div class="competitor-bucket-stats">
            <span>You <strong>{{ bucket.ourPageCountSum }}</strong></span>
            <span>Them <strong>{{ bucket.maxCompetitorPageCount }}</strong></span>
            <span class="gap-positive">+{{ bucket.gap }}</span>
          </div>
          <div class="competitor-bucket-actions" (click)="$event.stopPropagation()">
            <button
              type="button"
              class="btn btn--sm"
              *ngIf="!bucket.alreadyPillar"
              (click)="addPillarFromBucket(bucket)"
              [disabled]="pillars.length >= 12"
            >
              Add as pillar
            </button>
            <span class="muted" *ngIf="bucket.alreadyPillar">In your pillars</span>
          </div>
        </header>

        <table
          class="table table--dense competitor-table--nested"
          *ngIf="isCompetitorBucketExpanded(bucket.bucketId) && bucket.topicCount > 1"
        >
          <thead>
            <tr>
              <th>Topic</th>
              <th>You</th>
              <th>Best competitor</th>
              <th>Gap</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of bucket.topics">
              <td>
                <strong>{{ row.label }}</strong>
                <span class="muted competitor-slug">{{ row.topicSlug }}</span>
              </td>
              <td>{{ row.ourPageCount }}</td>
              <td>
                <strong>{{ row.maxCompetitorPageCount }}</strong>
                <span class="muted competitor-best-domain">{{ topCompetitorLabel(row) }}</span>
              </td>
              <td>
                <span [class.gap-positive]="row.gap > 0">+{{ row.gap }}</span>
              </td>
              <td>
                <button
                  type="button"
                  class="btn btn--sm"
                  *ngIf="!row.alreadyPillar"
                  (click)="addPillarFromCompetitor(row.topicSlug, row.label)"
                  [disabled]="pillars.length >= 12"
                >
                  Add as pillar
                </button>
                <span class="muted" *ngIf="row.alreadyPillar">In your pillars</span>
              </td>
            </tr>
          </tbody>
        </table>
      </article>
    </div>
    </div>
  </section>

<!-- The "start" modal was removed with K1: nothing opens it any more, because
     a domain with a committed plan now loads that plan instead of being asked
     which mode it wants. Dead markup, not a spare. -->

<!-- Add pillar modal -->
<div class="modal-backdrop" *ngIf="modal === 'addPillar'">
  <div class="modal card card--pad">
    <h2>Add pillar</h2>
    <input type="search" class="url-search input" placeholder="Search domain topics…" [(ngModel)]="addPillarSearch" />
    <ul class="topic-pick-list">
      <li *ngFor="let topic of filteredTopicPickerOptions" (click)="addPillarFromTopic(topic)">
        <div class="topic-pick-main">
          <strong>{{ topic.label }}</strong>
          <span class="topic-pick-parent muted" *ngIf="topic.topicType === 'child'">in {{ topic.parentLabel }}</span>
        </div>
        <div class="topic-pick-tags">
          <span class="badge topic-kind-tag" [ngClass]="topicKindTagClass(topic)">
            {{ topic.topicType === 'child' ? 'Child' : 'Parent' }}
          </span>
          <span class="muted">{{ topic.slug }} · authority {{ formatAuthorityScore(topic.authorityScore) }}</span>
        </div>
      </li>
    </ul>
    <p class="muted" *ngIf="filteredTopicPickerOptions.length === 0">No matching topics.</p>
    <button type="button" class="btn btn--link" (click)="closeModal()">Cancel</button>
  </div>
</div>

<!-- Merge modal -->
<div class="modal-backdrop" *ngIf="modal === 'merge'">
  <div class="modal card card--pad">
    <h2>Merge pillars</h2>
    <label class="field">
      <span class="field__label">Source (removed)</span>
      <select class="input" [(ngModel)]="mergeSourceSlug">
        <option *ngFor="let p of pillars" [value]="p.topicSlug">{{ p.label }}</option>
      </select>
    </label>
    <label class="field">
      <span class="field__label">Target (kept)</span>
      <select class="input" [(ngModel)]="mergeTargetSlug">
        <option *ngFor="let p of pillars" [value]="p.topicSlug">{{ p.label }}</option>
      </select>
    </label>
    <div class="modal-actions">
      <button type="button" class="btn btn--accent" (click)="confirmMerge()" [disabled]="mergeSourceSlug === mergeTargetSlug">Merge</button>
      <button type="button" class="btn btn--link" (click)="closeModal()">Cancel</button>
    </div>
  </div>
</div>

<!-- Split modal -->
<div class="modal-backdrop" *ngIf="modal === 'split'">
  <div class="modal card card--pad modal-wide">
    <h2>Split pillar</h2>
    <label class="field">
      <span class="field__label">New pillar label</span>
      <input class="input" type="text" [(ngModel)]="splitNewLabel" placeholder="e.g. Blog" />
    </label>
    <label class="field">
      <span class="field__label">New pillar slug (optional)</span>
      <input class="input" type="text" [(ngModel)]="splitNewSlug" placeholder="blog" />
    </label>
    <div class="split-mode">
      <label><input type="radio" name="splitMode" value="pathPrefix" [(ngModel)]="splitMode" /> By path prefix</label>
      <label><input type="radio" name="splitMode" value="childSlug" [(ngModel)]="splitMode" /> By child topic</label>
      <label><input type="radio" name="splitMode" value="manual" [(ngModel)]="splitMode" /> Manual selection</label>
    </div>
    <label class="field" *ngIf="splitMode === 'pathPrefix'">
      <span class="field__label">Path prefix</span>
      <input class="input" type="text" [(ngModel)]="splitPathPrefix" placeholder="/blog/" />
    </label>
    <label class="field" *ngIf="splitMode === 'childSlug'">
      <span class="field__label">Child topic slug</span>
      <select class="input" [(ngModel)]="splitChildSlug">
        <option value="">Select…</option>
        <option *ngFor="let slug of splitChildOptions" [value]="slug">{{ slug }}</option>
      </select>
    </label>
    <div *ngIf="splitMode === 'manual'" class="manual-split-list">
      <label *ngFor="let u of sourcePillarUrlsForSplit()" class="manual-split-row">
        <input type="checkbox" [checked]="splitManualUrlIds.includes(u.urlId)" (change)="toggleManualSplitUrl(u.urlId)" />
        {{ u.url }}
      </label>
    </div>
    <div class="modal-actions">
      <button type="button" class="btn btn--accent" (click)="confirmSplit()">Split</button>
      <button type="button" class="btn btn--link" (click)="closeModal()">Cancel</button>
    </div>
  </div>
</div>

<!-- Apply modal -->
<div class="modal-backdrop" *ngIf="modal === 'apply'">
  <div class="modal card card--pad">
    <h2>Apply organization</h2>
    <p *ngIf="preview">
      This will save <strong>{{ preview.summary.pillarCount }}</strong> pillars with
      <strong>{{ preview.summary.assignedUrlCount }}</strong> URL assignments.
      <strong>{{ preview.summary.unassignedUrlCount }}</strong> URLs will remain unclustered.
    </p>
    <p class="warning" *ngIf="status?.hasCommittedPlan">
      Replace mode will delete your existing plan clusters for this domain and write this layout.
    </p>
    <label class="field">
      <span class="field__label">Mode</span>
      <select class="input" [(ngModel)]="applyMode">
        <option value="replace">Replace existing plan</option>
        <option value="merge">Merge into existing plan</option>
      </select>
    </label>
    <div class="modal-actions">
      <button type="button" class="btn btn--accent" (click)="confirmApply()" [disabled]="applying">
        {{ applying ? 'Applying…' : 'Confirm apply' }}
      </button>
      <button type="button" class="btn btn--link" (click)="closeModal()" [disabled]="applying">Cancel</button>
      <button type="button" class="btn" (click)="goToInventory()" [disabled]="applying">View Inventory</button>
    </div>
  </div>
</div>
